import type { CSSProperties, ReactNode } from 'react';
import type { TutorialStep } from './model';

/*
 * Schematic illustrations for the help center: the app's parts drawn as simple shapes, in the
 * theme's tokens (so they follow light and dark) and without words (so they work in every
 * language). Decorative: the text next to them carries the meaning.
 */

const v = (name: string) => `var(--${name})`;
const fill = (name: string, opacity?: number): CSSProperties => ({
  fill: v(name),
  ...(opacity === undefined ? {} : { fillOpacity: opacity }),
});
const stroke = (name: string, width = 1.5, extra: CSSProperties = {}): CSSProperties => ({
  fill: 'none',
  stroke: v(name),
  strokeWidth: width,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  ...extra,
});

/** A text line placeholder. */
function Line({ x, y, w, tone = 'text-4' }: { x: number; y: number; w: number; tone?: string }) {
  return <rect x={x} y={y} width={w} height={4} rx={2} style={fill(tone, 0.7)} />;
}

/** A window: the app frame every tutorial illustration sits in. */
function Frame({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 320 200" className="h-auto w-full" aria-hidden focusable="false">
      <rect
        x={0.5}
        y={0.5}
        width={319}
        height={199}
        rx={10}
        style={{ ...fill('bg-1'), stroke: v('border-subtle') }}
      />
      <path d="M0.5 22 H319.5" style={stroke('border-subtle', 1)} />
      {[14, 26, 38].map((x) => (
        <circle key={x} cx={x} cy={11.5} r={3} style={fill('bg-4')} />
      ))}
      {children}
    </svg>
  );
}

/** The welcome hero: plans as lines rising over the horizon, with a range around one of them. */
export function HeroIllustration() {
  return (
    <svg viewBox="0 0 300 200" className="h-auto w-full" aria-hidden focusable="false">
      <defs>
        <linearGradient id="help-hero-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={v('accent')} stopOpacity={0} />
          <stop offset="1" stopColor={v('accent')} stopOpacity={0.18} />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={300} height={150} style={{ fill: 'url(#help-hero-sky)' }} />
      {[40, 75, 110].map((y) => (
        <path key={y} d={`M16 ${String(y)} H284`} style={stroke('chart-grid', 1)} />
      ))}
      {/* The range of outcomes around the first plan. */}
      <path
        d="M24 132 C80 126 120 112 168 92 C210 74 246 48 284 22 L284 92 C246 104 210 112 168 118 C120 124 80 130 24 132 Z"
        style={fill('chart-series-1', 0.16)}
      />
      <path
        d="M24 132 C82 128 124 116 170 104 C214 92 248 72 284 56"
        style={stroke('chart-series-1', 2.5)}
      />
      <path
        d="M24 132 C78 132 118 128 166 118 C212 108 250 94 284 84"
        style={stroke('chart-series-2', 2.5)}
      />
      <circle
        cx={284}
        cy={56}
        r={4.5}
        style={{ ...fill('chart-series-1'), stroke: v('bg-2'), strokeWidth: 2 }}
      />
      <circle
        cx={284}
        cy={84}
        r={4.5}
        style={{ ...fill('chart-series-2'), stroke: v('bg-2'), strokeWidth: 2 }}
      />
      {/* The Kasora motif: a disc on the horizon. */}
      <circle cx={150} cy={160} r={26} style={fill('accent')} />
      <circle cx={150} cy={160} r={18} style={stroke('bg-2', 2.5)} />
      <rect x={0} y={160} width={300} height={40} style={fill('accent')} />
      <rect x={0} y={160} width={300} height={40} style={fill('bg-0', 0.35)} />
    </svg>
  );
}

function Projects() {
  const variant = (y: number, series: string, items: number[]) => (
    <g>
      <circle cx={24} cy={y} r={3.5} style={fill(series)} />
      <Line x={34} y={y - 2} w={46} tone="text-2" />
      {items.map((w, i) => (
        <g key={i}>
          <rect x={30} y={y + 10 + i * 14} width={8} height={8} rx={2} style={fill('bg-4')} />
          <Line x={44} y={y + 12 + i * 14} w={w} />
        </g>
      ))}
    </g>
  );
  return (
    <Frame>
      <rect x={10} y={30} width={110} height={160} rx={6} style={fill('bg-2')} />
      {variant(44, 'chart-series-1', [48, 36, 42])}
      {variant(112, 'chart-series-2', [48, 30, 40])}
      {/* The shared salary: the same item under both variants, linked. */}
      <rect x={26} y={50} width={94} height={12} rx={3} style={fill('accent', 0.14)} />
      <rect x={26} y={118} width={94} height={12} rx={3} style={fill('accent', 0.14)} />
      <path
        d="M108 56 C118 56 118 124 108 124"
        style={stroke('accent', 1.5, { strokeDasharray: '3 3' })}
      />
      {/* The project's assumptions. */}
      <rect
        x={136}
        y={34}
        width={172}
        height={74}
        rx={8}
        style={{ ...fill('bg-2'), stroke: v('border-subtle') }}
      />
      <Line x={148} y={46} w={70} tone="text-2" />
      {[62, 76, 90].map((y) => (
        <g key={y}>
          <Line x={148} y={y} w={52} />
          <rect
            x={232}
            y={y - 4}
            width={64}
            height={12}
            rx={3}
            style={{ ...fill('bg-1'), stroke: v('border-strong') }}
          />
        </g>
      ))}
      <path
        d="M140 170 C176 166 210 150 236 132 S280 108 304 100"
        style={stroke('chart-series-1', 2)}
      />
      <path
        d="M140 170 C176 170 214 160 240 152 S284 138 304 134"
        style={stroke('chart-series-2', 2)}
      />
    </Frame>
  );
}

function Items() {
  const rows: [string, number][] = [
    ['chart-diverging-pos', 52],
    ['chart-diverging-neg', 40],
    ['chart-diverging-neg', 46],
    ['chart-series-3', 38],
    ['chart-series-4', 44],
  ];
  return (
    <Frame>
      <rect x={10} y={30} width={130} height={160} rx={6} style={fill('bg-2')} />
      {rows.map(([tone, w], i) => {
        const y = 44 + i * 28;
        const up = tone === 'chart-diverging-pos';
        return (
          <g key={i}>
            <rect
              x={18}
              y={y - 9}
              width={114}
              height={22}
              rx={5}
              style={i === 0 ? fill('bg-4') : fill('bg-2')}
            />
            <circle cx={30} cy={y + 2} r={6} style={fill(tone, 0.2)} />
            <path
              d={
                up
                  ? `M30 ${String(y + 5)} V${String(y - 1)} M27 ${String(y + 2)} L30 ${String(y - 1)} L33 ${String(y + 2)}`
                  : `M30 ${String(y - 1)} V${String(y + 5)} M27 ${String(y + 2)} L30 ${String(y + 5)} L33 ${String(y + 2)}`
              }
              style={stroke(tone, 1.5)}
            />
            <Line x={42} y={y} w={w} tone="text-2" />
          </g>
        );
      })}
      {/* The inspector for the selected item. */}
      <rect
        x={152}
        y={30}
        width={158}
        height={160}
        rx={6}
        style={{ ...fill('bg-2'), stroke: v('border-subtle') }}
      />
      <Line x={164} y={42} w={64} tone="text-1" />
      {[60, 80].map((y) => (
        <g key={y}>
          <Line x={164} y={y} w={40} />
          <rect
            x={226}
            y={y - 5}
            width={72}
            height={14}
            rx={3}
            style={{ ...fill('bg-1'), stroke: v('border-strong') }}
          />
        </g>
      ))}
      <rect x={164} y={100} width={134} height={78} rx={5} style={fill('bg-1')} />
      <path
        d="M172 166 C200 160 224 148 248 132 S284 112 292 108"
        style={stroke('chart-diverging-pos', 2)}
      />
      <path d="M172 166 H292" style={stroke('chart-axis', 1)} />
    </Frame>
  );
}

function Assumptions() {
  return (
    <Frame>
      <rect x={10} y={30} width={300} height={56} rx={6} style={fill('bg-2')} />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <Line x={20 + i * 74} y={42} w={40} />
          <rect
            x={20 + i * 74}
            y={54}
            width={64}
            height={18}
            rx={4}
            style={{ ...fill('bg-1'), stroke: v('border-strong') }}
          />
        </g>
      ))}
      {/* A rate that changes over time: fixed, then a step, then inflation plus a margin. */}
      <rect x={10} y={96} width={300} height={94} rx={6} style={fill('bg-2')} />
      <path d="M24 176 H296" style={stroke('chart-axis', 1)} />
      <path
        d="M24 150 H120 V128 H210 V116 C240 112 268 106 296 102"
        style={stroke('accent', 2.5)}
      />
      {[120, 210].map((x) => (
        <g key={x}>
          <path
            d={`M${String(x)} 108 V176`}
            style={stroke('border-strong', 1, { strokeDasharray: '2 3' })}
          />
          <rect x={x - 5} y={172} width={10} height={8} rx={2} style={fill('accent')} />
        </g>
      ))}
      <rect x={24} y={172} width={10} height={8} rx={2} style={fill('accent')} />
    </Frame>
  );
}

function Charts() {
  return (
    <Frame>
      <rect x={10} y={30} width={300} height={160} rx={6} style={fill('bg-2')} />
      {[60, 92, 124, 156].map((y) => (
        <path key={y} d={`M24 ${String(y)} H296`} style={stroke('chart-grid', 1)} />
      ))}
      <path
        d="M24 164 C70 160 110 150 150 136 S206 112 230 102"
        style={stroke('chart-series-1', 2.5)}
      />
      {/* The month being explained. */}
      <path d="M150 44 V176" style={stroke('chart-crosshair', 1, { strokeDasharray: '3 3' })} />
      <circle
        cx={150}
        cy={136}
        r={4.5}
        style={{ ...fill('chart-series-1'), stroke: v('bg-2'), strokeWidth: 2 }}
      />
      <rect
        x={164}
        y={40}
        width={128}
        height={86}
        rx={7}
        style={{ ...fill('bg-1'), stroke: v('border-strong') }}
      />
      <Line x={176} y={52} w={60} tone="text-1" />
      {[
        ['chart-diverging-pos', 66],
        ['chart-diverging-neg', 80],
        ['chart-diverging-neg', 94],
      ].map(([tone, y]) => (
        <g key={String(y)}>
          <Line x={176} y={Number(y)} w={46} />
          <rect
            x={250}
            y={Number(y)}
            width={30}
            height={4}
            rx={2}
            style={fill(String(tone), 0.9)}
          />
        </g>
      ))}
      <path d="M176 108 H280" style={stroke('border-subtle', 1)} />
      <Line x={176} y={114} w={40} tone="text-2" />
      <rect x={250} y={114} width={30} height={4} rx={2} style={fill('text-2')} />
    </Frame>
  );
}

function Compare() {
  return (
    <Frame>
      <rect x={10} y={30} width={300} height={160} rx={6} style={fill('bg-2')} />
      <path
        d="M24 150 C90 140 150 118 200 96 S260 64 296 46 L296 112 C260 120 220 126 200 128 C150 134 90 146 24 150 Z"
        style={fill('chart-series-2', 0.14)}
      />
      <path
        d="M24 150 C90 144 150 126 200 112 S262 86 296 78"
        style={stroke('chart-series-2', 2.5)}
      />
      <path
        d="M24 146 C80 140 120 132 160 124 S250 106 296 100"
        style={stroke('chart-series-1', 2.5)}
      />
      {/* Where one variant moves ahead for good. */}
      <path
        d="M144 127 l6 -6 l6 6 l-6 6 Z"
        style={{ ...fill('chart-diverging-pos'), stroke: v('bg-2'), strokeWidth: 1.5 }}
      />
      <path d="M24 178 H296" style={stroke('chart-axis', 1)} />
      {[200, 240, 280].map((x, i) => (
        <rect
          key={x}
          x={x - 8}
          y={178 - (i + 1) * 5}
          width={16}
          height={(i + 1) * 5}
          rx={2}
          style={fill('chart-diverging-pos', 0.55)}
        />
      ))}
    </Frame>
  );
}

function Share() {
  return (
    <Frame>
      {/* A report page. */}
      <rect
        x={30}
        y={34}
        width={112}
        height={150}
        rx={6}
        style={{ ...fill('bg-2'), stroke: v('border-strong') }}
      />
      <rect x={42} y={46} width={14} height={14} rx={4} style={fill('accent')} />
      <Line x={62} y={50} w={48} tone="text-1" />
      <rect x={42} y={70} width={88} height={48} rx={4} style={fill('bg-1')} />
      <path d="M48 110 C70 106 88 96 104 88 S122 80 126 78" style={stroke('chart-series-1', 1.8)} />
      <path
        d="M48 110 C70 110 90 104 106 100 S122 96 126 94"
        style={stroke('chart-series-2', 1.8)}
      />
      {[128, 138, 148, 158, 168].map((y, i) => (
        <Line key={y} x={42} y={y} w={i % 2 ? 72 : 88} />
      ))}
      {/* A read-only link, and the same plan on another device. */}
      <rect
        x={168}
        y={52}
        width={124}
        height={30}
        rx={15}
        style={{ ...fill('accent', 0.14), stroke: v('accent') }}
      />
      <path
        d="M186 67 h6 M183 62 a5 5 0 0 0 0 10 h3 M195 62 a5 5 0 0 1 0 10 h-3"
        style={stroke('accent', 1.6)}
      />
      <Line x={206} y={65} w={70} tone="accent" />
      <rect
        x={196}
        y={104}
        width={72}
        height={80}
        rx={9}
        style={{ ...fill('bg-2'), stroke: v('border-strong') }}
      />
      <rect x={204} y={116} width={56} height={34} rx={4} style={fill('bg-1')} />
      <path d="M208 144 C222 140 236 132 256 124" style={stroke('chart-series-1', 1.6)} />
      {[158, 168].map((y) => (
        <Line key={y} x={204} y={y} w={48} />
      ))}
      <path
        d="M150 140 C164 140 170 136 186 130"
        style={stroke('text-4', 1.5, { strokeDasharray: '3 3' })}
      />
    </Frame>
  );
}

const STEP_ILLUSTRATION: Record<TutorialStep, () => ReactNode> = {
  projects: Projects,
  items: Items,
  assumptions: Assumptions,
  charts: Charts,
  compare: Compare,
  share: Share,
};

export function TutorialIllustration({ step }: { step: TutorialStep }) {
  const Illustration = STEP_ILLUSTRATION[step];
  return <Illustration />;
}
